/** @format */

import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { AiOutlineGoogle } from "react-icons/ai";
import { FaFacebook, FaWhatsapp } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import FadeLoader from "react-spinners/FadeLoader";
import api from "../api/api";
import Footer from "../components/Footer";
import Headers from "../components/Headers";
import { messageClear } from "../store/reducers/homeReducer";
import { useCategories } from "../utils/Categories";
import { address, email, phone } from "../utils/utils";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
//const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const DASHBOARD_URL = process.env.REACT_APP_DASHBOARD_URL;

const INITIAL_FORM_STATE = {
  name: "",
  firstName: "",
  email: "",
  telephone: "",
  subject: "",
  message: "",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Full-screen loading overlay */
const LoadingOverlay = () => (
  <div
    role='status'
    aria-busy='true'
    aria-label='Chargement en cours'
    className='fixed inset-0 z-[99999] flex items-center justify-center bg-gray-500/70'
  >
    <FadeLoader />
  </div>
);

/** Single social / contact button rendered as a proper anchor */
const SocialContactButton = ({ href, icon: Icon, label, colorClass }) => (
  <a
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    className={`flex items-center gap-3 w-full px-6 py-2.5 rounded-md text-white font-medium
      transition-shadow hover:shadow-lg mb-3 ${colorClass}`}
  >
    <Icon size={18} aria-hidden='true' />
    <span>{label}</span>
  </a>
);

/** Right-side contact info block */
const ContactInfoPanel = ({ t }) => (
  <div className='w-full h-full p-6 flex flex-col justify-between'>
    <div>
      <img
        className='w-full mt-10 md-lg:mt-0 object-contain'
        src='/images/grow.png'
        alt='Illustration de croissance avec Gobymall'
      />
      <p className='w-full text-8xl md-lg:text-6xl font-bold leading-tight mt-4'>
        {t("WITH US")}
      </p>
      <div className='w-full h-[4px] bg-orange-400 mt-2' />
    </div>

    <div className='mt-6 space-y-3'>
      {[
        { label: "Telephone", value: `+${phone}` },
        { label: "Adresse", value: address },
        { label: "Email", value: email },
      ].map(({ label, value }) => (
        <p key={label} className='text-xl sm:text-sm font-sans'>
          {label} : <strong className='text-orange-400'>{value}</strong>
        </p>
      ))}
    </div>
  </div>
);

/** Step 2 — success confirmation */
const SuccessStep = ({ serverMessage }) => (
  <div className='max-w-lg mx-auto my-12 p-8 border border-slate-200 rounded-xl shadow-md font-sans space-y-4'>
    <p className='text-green-600 font-semibold text-lg'>
      Nous vous remercions pour votre message.
    </p>
    <p className='text-slate-600'>
      Nous vous répondrons dans les plus brefs délais.
    </p>
    <p className='text-slate-600'>
      Pour vendre, inscrivez-vous{" "}
      <Link
        to={`${DASHBOARD_URL}/Register`}
        className='text-blue-500 underline hover:text-blue-700 transition-colors'
      >
        ici
      </Link>
      .
    </p>
    <p className='text-slate-600'>
      Vous pouvez aussi nous contacter via WhatsApp au{" "}
      <a
        href={`https://wa.me/${phone}`}
        className='text-orange-400 font-semibold hover:underline'
        target='_blank'
        rel='noopener noreferrer'
      >
        +{phone}
      </a>
      .
    </p>
    {serverMessage && (
      <p className='text-sm text-slate-500 italic'>{serverMessage}</p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Custom hooks
// ---------------------------------------------------------------------------

/** Handles Redux auth notifications (toasts) */
const useAuthNotifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loader, successMessage, messageError } = useSelector(
    (state) => state.auth,
  );

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
      navigate("/login");
    }
    if (messageError) {
      toast.error(messageError);
      dispatch(messageClear());
    }
  }, [successMessage, messageError, dispatch, navigate]);

  return { loader };
};

/** Handles contact form state, submission and multi-step flow */
const useContactForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [step, setStep] = useState(1);
  const [serverMessage, setServerMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handlePhoneChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, telephone: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await api.post(`api/contact`, formData);
      setServerMessage(data.message);
      setStep(2);
    } catch (error) {
      setServerMessage(
        error.response?.data?.message ?? "Une erreur est survenue.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    step,
    serverMessage,
    isSubmitting,
    handleChange,
    handlePhoneChange,
    handleSubmit,
  };
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const Contact = () => {
  const { t } = useTranslation();
  const { loader } = useAuthNotifications();
  const { categories, clientLocation } = useCategories();

  const {
    formData,
    step,
    serverMessage,
    isSubmitting,
    handleChange,
    handlePhoneChange,
    handleSubmit,
  } = useContactForm();

  const inputClass =
    "w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md transition-colors";

  const socialLinks = [
    {
      href: "https://facebook.com",
      icon: FaFacebook,
      label: t("Facebook Message"),
      colorClass: "bg-indigo-500 hover:shadow-indigo-500/40",
    },
    {
      href: `mailto:${email}`,
      icon: AiOutlineGoogle,
      label: t("Send us Email"),
      colorClass: "bg-orange-500 hover:shadow-orange-500/40",
    },
    {
      href: `https://wa.me/${phone}`,
      icon: FaWhatsapp,
      label: t("Whatsapp Message"),
      colorClass: "bg-green-500 hover:shadow-green-500/40",
    },
  ];

  return (
    <div className='min-h-screen flex flex-col'>
      {loader && <LoadingOverlay />}

      <Headers categories={categories} clientLocation={clientLocation} />

      <main className='flex-1'>
        {step === 1 && (
          <div className='bg-slate-200 mt-4 py-10 px-4 sm:px-3'>
            <div className='grid grid-cols-2 md-lg:grid-cols-1 w-[90%] sm:w-full mx-auto bg-white rounded-xl shadow-sm overflow-hidden'>
              {/* Left — Form */}
              <div className='p-8 sm:p-4 border-r border-slate-100 md-lg:border-r-0 md-lg:border-b'>
                <h2 className='text-center text-xl text-slate-600 font-bold mb-5'>
                  {t("Contact US")}
                </h2>

                <form
                  onSubmit={handleSubmit}
                  className='text-slate-800'
                  noValidate
                >
                  <div className='flex flex-col gap-1 mb-3'>
                    <input
                      required
                      type='text'
                      name='firstName'
                      id='firstName'
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder={t("Your firstName")}
                      className={inputClass}
                    />
                  </div>

                  <div className='flex flex-col gap-1 mb-3'>
                    <input
                      required
                      type='text'
                      name='name'
                      id='name'
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t("Your LastName")}
                      className={inputClass}
                    />
                  </div>

                  <div className='flex flex-col gap-1 mb-3'>
                    <input
                      required
                      type='email'
                      name='email'
                      id='email'
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t("Your email")}
                      className={inputClass}
                    />
                  </div>

                  <div className='flex flex-col gap-1 mb-3'>
                    <label
                      htmlFor='telephone'
                      className='text-sm text-slate-600'
                    >
                      {t("Your Phone Number")}
                    </label>
                    <PhoneInput
                      country='tg'
                      value={formData.telephone}
                      onChange={handlePhoneChange}
                      enableSearch
                      inputStyle={{
                        width: "100%",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "0.375rem",
                      }}
                    />
                  </div>

                  <div className='flex flex-col gap-1 mb-3'>
                    <input
                      required
                      type='text'
                      name='subject'
                      id='subject'
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder={t("Your subject")}
                      className={inputClass}
                    />
                  </div>

                  <div className='flex flex-col gap-1 mb-4'>
                    <textarea
                      rows={6}
                      name='message'
                      id='message'
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t("write your message")}
                      className={inputClass}
                    />
                  </div>

                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60
                      disabled:cursor-not-allowed text-white py-2.5 rounded-md font-medium
                      transition-colors'
                  >
                    {isSubmitting ? t("Sending...") : t("SUBMIT")}
                  </button>
                </form>

                {/* Divider */}
                <div className='flex items-center my-4 gap-3'>
                  <div className='flex-1 h-px bg-slate-200' />
                  <span className='text-sm text-slate-500'>ou</span>
                  <div className='flex-1 h-px bg-slate-200' />
                </div>

                {/* Social links */}
                <div className='flex flex-col'>
                  {socialLinks.map(({ href, icon, label, colorClass }) => (
                    <SocialContactButton
                      key={href}
                      href={href}
                      icon={icon}
                      label={label}
                      colorClass={colorClass}
                    />
                  ))}
                </div>
              </div>

              {/* Right — Info panel */}
              <ContactInfoPanel t={t} />
            </div>
          </div>
        )}

        {step === 2 && <SuccessStep serverMessage={serverMessage} />}
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
